import React, { ReactElement, useState, useMemo } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import { connect } from 'react-redux'
import { Tooltip } from '@material-ui/core'
import { ReactComponent as AttachmentIcon } from 'assets/img/attachment-icon.svg'
import { ReactComponent as DeleteJobIcon } from 'assets/img/management-delete-job-icon.svg'
import { ReactComponent as DuplicateTaskIcon } from 'assets/img/management-duplicate-task-icon.svg'
import {
  deleteTask,
  updateTask,
  updateTaskAssignees,
} from 'reducers/managementUI/reducer'
import {
  FILTER_TYPE_ASSIGNED_USER,
  FILTER_TYPE_ASSIGNED_USER_GROUP,
} from 'constants/managementUI'
import { Flex } from 'components/common/styles'
import UsersListComponent from 'components/common/UsersList/UsersList'
import {
  ConfigureCol,
  ContextMenuItem,
  ContextMenuItemSecondary,
  CreatedByCol,
  ManagementUICol,
  Label,
  MediaCol,
  MUIMenu,
  NameColTask,
  OtherCol,
  OverdueCol,
  RecurrenceCol,
  SingleJobTask,
  StatusIndicatorCol,
  StyledUnitsCol,
  TasksCompletedCol,
} from '../../ManagementUI.styles'
import { SingleTaskProps, User } from './SingleTask.types'
import { MoreOptionsIcon } from '../SingleJob/SingleJob.styles'
import PreviewInputValues from '../PreviewInputValues/PreviewInputValues'
import messages from '../../translate'

const SingleTask = ({
  intl: { formatMessage },
  task,
  position,
  assigneeColDisplay,
  recurrenceColDisplay,
  mediaColDisplay,
  jobIdColDisplay,
  createdByColDisplay,
  modifiedByColDisplay,
  tasksCompletedColDisplay,
  dueDateColDisplay,
  unitsColDisplay,
  endDateColDisplay,
  createdDateColDisplay,
  completedDateColDisplay,
  startDateColDisplay,
  getStatusIcon,
  updateTaskAssignees,
  jobId,
  setJobContextMenuPosition,
  updateTask,
  deleteTask,
  setIsDialogOpened,
  setConfirmDialogSettings,
  renderUnitsContent,
  isJobCompleted,
  isSchedulingTab,
  is_future,
}: SingleTaskProps) => {
  const {
    id: taskId,
    title,
    state: taskState,
    duplicated,
    duplicable,
    assigned_users,
    assigned_user_groups,
    media,
    modified_by,
    locuses_and_groups,
    needs_locus,
    needs_locus_group,
  } = task
  const [
    taskContextMenuAnchor,
    setTaskContextMenuAnchor,
  ] = useState<Element | null>(null)
  const isCompleted = useMemo(() => taskState === 'completed', [taskState])

  const isAllowedToChange = useMemo(
    () => !is_future && !isCompleted && !isSchedulingTab,
    [is_future, isSchedulingTab, isCompleted],
  )
  const isContextMenuAllowed = useMemo(() => !is_future && duplicable, [
    is_future,
    duplicable,
  ])

  const onOpenDeleteTaskDialog = (
    popupTitle: string,
    popupMessage: ReactElement,
    popupAction: () => () => void,
  ) => {
    setIsDialogOpened(true)
    setTaskContextMenuAnchor(null)
    setConfirmDialogSettings({
      popupAction,
      popupTitle,
      popupMessage,
      needsDeletionReason: false,
    })
  }

  const updateAssignees = (users: Array<User>, groups: Array<User>) => {
    updateTaskAssignees({
      params: {
        id: taskId,
      },
      additionalParams: {
        jobId,
        taskId,
      },
      data: {
        assigned_users: users,
        assigned_user_groups: groups,
      },
      successMessage: formatMessage(messages.reassignSuccessMessage),
      errorMessage: formatMessage(messages.reassignErrorMessage),
    })
  }

  const onRemoveTaskAssignee = (assigneeId: number, type: string) => {
    const updatedUsersArr =
      type === FILTER_TYPE_ASSIGNED_USER
        ? assigned_users.filter(({ id }) => id !== assigneeId)
        : assigned_users
    const updatedUserGroupsArr =
      type === FILTER_TYPE_ASSIGNED_USER_GROUP
        ? assigned_user_groups.filter(({ id }) => id !== assigneeId)
        : assigned_user_groups

    updateAssignees(updatedUsersArr, updatedUserGroupsArr)
  }

  const onConfirmDeleteTask = () => {
    setIsDialogOpened(false)
    deleteTask({
      id: taskId,
      params: {
        jobId,
      },
      successMessage: formatMessage(messages.deleteTaskSuccessMessage),
      errorMessage: formatMessage(messages.deleteTaskErrorMessage),
    })
  }

  const onOpenTaskContextMenu = (event: React.MouseEvent, anchor: Element) => {
    event.preventDefault()
    setJobContextMenuPosition({
      left: event.pageX,
      top: event.pageY,
    })
    setTaskContextMenuAnchor(anchor)
  }

  const isContextMenuActive = useMemo(
    () => isContextMenuAllowed && (duplicated || !isJobCompleted),
    [isContextMenuAllowed, duplicated, isJobCompleted],
  )

  return (
    <SingleJobTask
      onContextMenu={event => {
        if (isContextMenuAllowed) {
          onOpenTaskContextMenu(
            event,
            document.querySelectorAll('.job-context-menu-anchor-el')[0],
          )
        }
      }}>
      <Flex>
        <NameColTask>
          <Tooltip
            title={title}
            arrow
            classes={{ popper: 'management-ui-tooltip' }}>
            <Label>
              {position}. {title}
            </Label>
          </Tooltip>
        </NameColTask>
        <OverdueCol>
          {(duplicated || (duplicable && !isJobCompleted)) && (
            <DuplicateTaskIcon />
          )}
        </OverdueCol>
        <OtherCol />
        {assigneeColDisplay && (
          <OtherCol>
            <UsersListComponent
              users={assigned_users}
              groups={assigned_user_groups}
              onAddUser={isAllowedToChange ? updateAssignees : undefined}
              onRemoveUser={
                isAllowedToChange ? onRemoveTaskAssignee : undefined
              }
            />
          </OtherCol>
        )}
        {tasksCompletedColDisplay && <TasksCompletedCol>-</TasksCompletedCol>}
        {recurrenceColDisplay && <RecurrenceCol>-</RecurrenceCol>}
        {mediaColDisplay && (
          <MediaCol>
            {media ? (
              <>
                <Label>{media}</Label>
                <AttachmentIcon />
              </>
            ) : (
              '-'
            )}
          </MediaCol>
        )}
        {jobIdColDisplay && <ManagementUICol>-</ManagementUICol>}
        {createdDateColDisplay && <ManagementUICol>-</ManagementUICol>}
        {createdByColDisplay && <CreatedByCol>-</CreatedByCol>}
        {modifiedByColDisplay && (
          <OtherCol>
            <UsersListComponent hideNoAssigneesIcon users={modified_by} />
          </OtherCol>
        )}
        {unitsColDisplay && (
          <StyledUnitsCol justify="left" align="center">
            <Tooltip
              title={renderUnitsContent(
                locuses_and_groups,
                needs_locus,
                needs_locus_group,
              )}
              placement="left"
              arrow
              classes={{ popper: 'management-ui-tooltip' }}>
              {renderUnitsContent(
                locuses_and_groups,
                needs_locus,
                needs_locus_group,
              )}
            </Tooltip>
          </StyledUnitsCol>
        )}
        {startDateColDisplay && <ManagementUICol>-</ManagementUICol>}
        {dueDateColDisplay && <ManagementUICol>-</ManagementUICol>}
        {endDateColDisplay && (
          <OtherCol>
            <Label>-</Label>
          </OtherCol>
        )}
        {completedDateColDisplay && <ManagementUICol>-</ManagementUICol>}
        {!!taskState && (
          <StatusIndicatorCol>
            <Tooltip title={formatMessage(messages[taskState])}>
              <div>{getStatusIcon(taskState)}</div>
            </Tooltip>
          </StatusIndicatorCol>
        )}
        <ConfigureCol>
          {isContextMenuAllowed && !isSchedulingTab && (
            <MoreOptionsIcon
              isMenuDisabled={!isContextMenuActive}
              onClick={event => {
                onOpenTaskContextMenu(event, event.currentTarget)
              }}>
              more_vert
            </MoreOptionsIcon>
          )}
        </ConfigureCol>
      </Flex>
      <PreviewInputValues previewInputs={task.preview_inputs} />
      {isContextMenuActive && (
        <MUIMenu
          anchorEl={taskContextMenuAnchor}
          open={!!taskContextMenuAnchor}
          onClose={() => {
            setTaskContextMenuAnchor(null)
          }}>
          {!isJobCompleted && (
            <ContextMenuItemSecondary
              onClick={() => {
                updateTask({
                  task,
                  params: {
                    jobId,
                  },
                  action: 'duplicate',
                  successMessage: formatMessage(messages.duplicateMessage),
                  errorMessage: formatMessage(messages.duplicateErrorMessage),
                })
                setTaskContextMenuAnchor(null)
              }}>
              <DuplicateTaskIcon />
              {formatMessage(messages.duplicate)}
            </ContextMenuItemSecondary>
          )}
          {duplicated && (
            <ContextMenuItem
              onClick={() => {
                onOpenDeleteTaskDialog(
                  formatMessage(messages.delete),
                  <FormattedMessage
                    {...messages.confirmTaskMessage}
                    values={{
                      action: 'delete',
                      entry: <strong>{title}</strong>,
                    }}
                  />,
                  () => () => {
                    onConfirmDeleteTask()
                  },
                )
              }}>
              <DeleteJobIcon />
              {formatMessage(messages.delete)}
            </ContextMenuItem>
          )}
        </MUIMenu>
      )}
    </SingleJobTask>
  )
}

export default connect(null, {
  updateTaskAssignees,
  updateTask,
  deleteTask,
})(injectIntl(SingleTask))
